/**
 * Languages the signed-in browser last started a recording with.
 * Lite instant-start reads this before the workspace default, so a Dutch
 * workspace does not override an English recording the person just made.
 * Storage failures (private mode, blocked storage) are ignored.
 */

const STORAGE_KEY = "lexicanon:last-used-languages"

export interface ILastUsedLanguages {
  languages: string[]
  autoDetect: boolean
}

export interface IRememberLanguagesInput {
  languages?: readonly string[] | null
  autoDetect?: boolean
}

function storage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") return null
    return localStorage
  } catch {
    return null
  }
}

function isLastUsedLanguages(value: unknown): value is ILastUsedLanguages {
  if (typeof value !== "object" || value === null) return false
  if (!("languages" in value) || !("autoDetect" in value)) return false
  const { languages, autoDetect } = value
  if (typeof autoDetect !== "boolean" || !Array.isArray(languages)) return false
  return languages.every((code) => typeof code === "string")
}

export function readLastUsedLanguages(): ILastUsedLanguages | null {
  const store = storage()
  if (!store) return null
  try {
    const raw = store.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isLastUsedLanguages(parsed)) return null
    return { languages: [...parsed.languages], autoDetect: parsed.autoDetect }
  } catch {
    return null
  }
}

export function rememberLastUsedLanguages(cfg: IRememberLanguagesInput): void {
  const store = storage()
  if (!store) return
  const languages = (cfg.languages ?? [])
    .filter((code): code is string => typeof code === "string" && code.trim().length > 0)
    .map((code) => code.trim())
  const autoDetect = cfg.autoDetect === true || languages.length === 0
  const stored: ILastUsedLanguages = {
    languages: autoDetect ? [] : languages,
    autoDetect,
  }
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // Storage can throw in private mode or when the quota is full.
  }
}
